<template>
  <div class="machine-detail-page">
    <!-- Loading State -->
    <div v-if="pending" class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-3"></div>
        <p class="text-gray-600">{{ $t('common.loading', 'Loading...') }}</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center max-w-md mx-auto px-4">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 mb-2">{{ $t('machineDetails.error.title', 'Machine Not Found') }}</h2>
        <p class="text-gray-600 mb-4">{{ $t('machineDetails.error.message', 'The machine you are looking for does not exist or has been removed.') }}</p>
        <NuxtLink to="/machines" class="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700">
          ← {{ $t('machineDetails.browseAll', 'Back to Catalog') }}
        </NuxtLink>
      </div>
    </div>

    <!-- Machine Detail Content -->
    <div v-else-if="machine">
      <!-- Compact Header Bar -->
      <div class="bg-gray-800 text-white print:hidden">
        <div class="container mx-auto px-4 py-4">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <!-- Breadcrumb -->
            <nav class="flex items-center text-sm">
              <NuxtLink to="/" class="text-gray-400 hover:text-white">{{ $t('nav.home', 'Home') }}</NuxtLink>
              <span class="mx-2 text-gray-500">/</span>
              <NuxtLink to="/machines" class="text-gray-400 hover:text-white">{{ $t('nav.machines', 'Machines') }}</NuxtLink>
              <span class="mx-2 text-gray-500">/</span>
              <span class="text-white truncate max-w-[200px]">{{ machine.reference_code }}</span>
            </nav>
            <!-- Quick Actions -->
            <div class="flex items-center gap-2">
              <a :href="`${apiBaseUrl}/machines/${machine.id}/pdf`" target="_blank" 
                 class="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded text-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                PDF
              </a>
              <button @click="printPage" class="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded text-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
                </svg>
                {{ $t('machineDetails.cta.print', 'Print') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="bg-gradient-to-b from-slate-50 to-slate-100">
        <div class="container mx-auto px-4 py-8">
          <!-- Title & Status Row -->
          <div class="bg-white rounded-xl shadow-sm p-6 mb-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <div class="flex items-center gap-3 mb-2">
                <span v-if="machine.brand" class="text-sm font-semibold text-blue-600 uppercase">{{ machine.brand.name }}</span>
                <span class="text-sm text-gray-500">REF: {{ machine.reference_code }}</span>
              </div>
              <h1 class="text-2xl lg:text-3xl font-bold text-gray-900">{{ machine.title }}</h1>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <!-- Availability Badge -->
              <span :class="getAvailabilityBadgeStyles(machine.availability)" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold rounded">
                <span :class="getAvailabilityDotClass(machine.availability)" class="w-2 h-2 rounded-full"></span>
                {{ getMachineAvailabilityLabel(machine.availability, machine.available_from) }}
              </span>
              <span v-if="machine.year_manufactured" class="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded">
                {{ machine.year_manufactured }}
              </span>
              <span v-if="machine.condition" class="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded capitalize">
                {{ machine.condition }}
              </span>
            </div>
          </div>

          <!-- Two Column Layout: Gallery + Info (60/40 Split) -->
          <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <!-- Left: Image Gallery -->
            <div class="bg-white rounded-xl shadow-sm p-4 lg:col-span-3">
              <!-- Main Image -->
              <div class="relative aspect-[4/3] bg-gray-50 rounded-lg overflow-hidden mb-3 border border-gray-200">
                <img
                  v-if="selectedImage"
                  :src="selectedImage"
                  :alt="machine.title"
                  class="w-full h-full object-cover cursor-pointer"
                  @click="openLightbox(selectedImageIndex)"
                  @error="handleImageError"
                />
                <div v-else class="w-full h-full flex items-center justify-center">
                  <svg class="w-20 h-20 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                </div>

                <!-- SOLD Overlay -->
                <div v-if="isSold" class="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/30">
                  <div class="bg-red-600 text-white font-black text-3xl px-6 py-3 transform -rotate-12 border-2 border-white">
                    SOLD
                  </div>
                </div>

                <!-- Image Counter -->
                <div v-if="machineImages.length > 1" class="absolute bottom-3 right-3 bg-black/60 text-white px-3 py-1 rounded text-sm">
                  {{ selectedImageIndex + 1 }} / {{ machineImages.length }}
                </div>

                <!-- Navigation -->
                <button v-if="machineImages.length > 1" @click.stop="prevImage" class="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                </button>
                <button v-if="machineImages.length > 1" @click.stop="nextImage" class="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </button>
              </div>

              <!-- Thumbnails -->
              <div v-if="machineImages.length > 1" class="flex gap-2 overflow-x-auto pb-2">
                <button
                  v-for="(image, index) in machineImages"
                  :key="index"
                  @click="selectedImageIndex = index"
                  class="w-16 h-16 flex-shrink-0 rounded overflow-hidden border-2"
                  :class="selectedImageIndex === index ? 'border-blue-600' : 'border-gray-200 hover:border-gray-400'"
                >
                  <img :src="getImageUrl(image)" :alt="`${machine.title} - ${index + 1}`" class="w-full h-full object-cover" @error="handleImageError" />
                </button>
              </div>

              <!-- Share & Social Links -->
              <div class="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 print:hidden">
                <p class="text-sm font-medium text-blue-800 mb-3">{{ $t('machineDetails.hero.share', 'Share this machine') }}</p>
                <div class="flex flex-wrap gap-2">
                  <!-- Facebook -->
                  <a :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`" target="_blank" 
                     class="w-9 h-9 bg-[#1877F2] hover:bg-[#166FE5] rounded flex items-center justify-center text-white" title="Share on Facebook">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <!-- WhatsApp -->
                  <a :href="`https://wa.me/?text=${encodeURIComponent(machine.title + ' - ' + currentUrl)}`" target="_blank" 
                     class="w-9 h-9 bg-[#25D366] hover:bg-[#20BD5A] rounded flex items-center justify-center text-white" title="Share on WhatsApp">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                  <!-- Instagram (Company page) -->
                  <a href="https://www.instagram.com/technomacsnc" target="_blank" 
                     class="w-9 h-9 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 rounded flex items-center justify-center text-white" title="Follow on Instagram">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <!-- X (Twitter) -->
                  <a :href="`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(machine.title)}`" target="_blank" 
                     class="w-9 h-9 bg-black hover:bg-gray-800 rounded flex items-center justify-center text-white" title="Share on X">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                </div>
              </div>
            </div>

            <!-- Right: Key Info + CTA -->
            <div class="space-y-4 lg:col-span-2">
              <!-- Quick Specs -->
              <div class="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h2 class="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  {{ $t('machineDetails.sections.technicalSpecs', 'Technical Specifications') }}
                </h2>
                <div class="grid grid-cols-2 gap-3">
                  <div v-if="machine.category" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ $t('machineDetails.specifications.category', 'Category') }}</p>
                    <p class="font-bold text-slate-800">{{ machine.category.name }}</p>
                  </div>
                  <div v-if="machine.brand" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ $t('machineDetails.specifications.brand', 'Brand') }}</p>
                    <p class="font-bold text-slate-800">{{ machine.brand.name }}</p>
                  </div>
                  <div v-if="machine.machine_model" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ $t('machineDetails.specifications.model', 'Model') }}</p>
                    <p class="font-bold text-slate-800">{{ machine.machine_model }}</p>
                  </div>
                  <div v-if="machine.year_manufactured" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ $t('machineDetails.specifications.year', 'Year') }}</p>
                    <p class="font-bold text-slate-800">{{ machine.year_manufactured }}</p>
                  </div>
                  <div v-if="machine.printing_colors_label" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ $t('machineDetails.specifications.printingColors', 'Colors') }}</p>
                    <p class="font-bold text-slate-800">{{ machine.printing_colors_label }}</p>
                  </div>
                  <div v-if="machine.location" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ $t('machineDetails.specifications.location', 'Location') }}</p>
                    <p class="font-bold text-slate-800">{{ machine.location }}</p>
                  </div>
                </div>
              </div>

              <!-- CTA Buttons -->
              <div class="bg-white rounded-xl shadow-sm p-5 border border-gray-100 print:hidden">
                <div v-if="isSold" class="w-full py-4 px-6 bg-red-50 border-2 border-red-200 text-red-700 font-bold rounded-lg flex items-center justify-center gap-2">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {{ $t('machineDetails.cta.machineSold', 'THIS MACHINE HAS BEEN SOLD') }}
                </div>
                <!-- Buttons side by side -->
                <div v-else class="flex gap-3">
                  <button @click="openInquiryModal" class="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    {{ $t('machineDetails.cta.requestQuote', 'REQUEST QUOTE') }}
                  </button>
                  <a href="https://wa.me/393289722811" target="_blank" class="flex-1 py-3 px-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#20BD5A] hover:to-[#0d7d6e] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-500/25">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {{ $t('machineDetails.cta.contactWhatsApp', 'WhatsApp') }}
                  </a>
                </div>
              </div>

              <!-- Company Contact Box -->
              <div class="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm">
                <h3 class="font-semibold text-blue-900 mb-2">{{ $t('machineDetails.contact.title', 'Need more information?') }}</h3>
                <p class="text-sm text-blue-800 mb-3">{{ $t('machineDetails.contact.desc', 'Our team is ready to assist you with detailed specs, pricing, and arrange a visit.') }}</p>
                <div class="flex items-center gap-4 text-sm">
                  <a href="mailto:info@technomac-snc.com" class="text-blue-700 hover:underline flex items-center gap-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    Email
                  </a>
                  <a href="tel:+393331234567" class="text-blue-700 hover:underline flex items-center gap-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    Phone
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Technical Specifications Section -->
      <div v-if="hasTechnicalSpecs || allFeatures.length > 0 || machine.description" class="bg-gradient-to-b from-slate-100 to-slate-200 py-10">
        <div class="container mx-auto px-4">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Technical Specs Table -->
            <div v-if="hasTechnicalSpecs" class="bg-white rounded-xl shadow-lg overflow-hidden">
              <div class="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4">
                <h2 class="text-white font-bold uppercase tracking-wide">{{ $t('machineDetails.sections.technicalSpecs', 'Technical Specifications') }}</h2>
              </div>
              <table class="w-full text-sm">
                <tbody class="divide-y divide-gray-200">
                  <tr v-if="machine.machine_model">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700 w-1/3">{{ $t('machineDetails.specifications.model', 'Model') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.machine_model }}</td>
                  </tr>
                  <tr v-if="machine.machine_type">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.type', 'Type') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.machine_type }}</td>
                  </tr>
                  <tr v-if="machine.max_sheet_size">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.maxSheetSize', 'Max Sheet Size') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.max_sheet_size }}</td>
                  </tr>
                  <tr v-if="machine.min_sheet_size">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.minSheetSize', 'Min Sheet Size') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.min_sheet_size }}</td>
                  </tr>
                  <tr v-if="machine.max_printing_area">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.maxPrintingArea', 'Max Print Area') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.max_printing_area }}</td>
                  </tr>
                  <tr v-if="machine.plate_size">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.plateSize', 'Plate Size') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.plate_size }}</td>
                  </tr>
                  <tr v-if="machine.max_printing_speed">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.maxPrintingSpeed', 'Max Speed') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.max_printing_speed }}</td>
                  </tr>
                  <tr v-if="machine.operating_speed">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.operatingSpeed', 'Operating Speed') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.operating_speed }}</td>
                  </tr>
                  <tr v-if="machine.power_requirement">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.powerRequirement', 'Power') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.power_requirement }}</td>
                  </tr>
                  <tr v-if="machine.machine_dimensions">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.dimensions', 'Dimensions') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.machine_dimensions }}</td>
                  </tr>
                  <tr v-if="machine.machine_weight">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700">{{ $t('machineDetails.specifications.weight', 'Weight') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ machine.machine_weight }} kg</td>
                  </tr>
                  <!-- Additional Specs from JSON -->
                  <template v-if="parsedSpecifications">
                    <tr v-for="(value, key) in parsedSpecifications" :key="key" v-show="!isArrayValue(value)">
                      <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700 capitalize">{{ formatSpecKey(key) }}</td>
                      <td class="px-4 py-3 text-gray-900">{{ value }}</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <!-- Features & Description -->
            <div :class="hasTechnicalSpecs ? 'space-y-6' : 'space-y-6 lg:col-span-2'">
              <!-- Features -->
              <div v-if="allFeatures.length > 0" class="bg-white rounded-xl shadow-lg overflow-hidden">
                <div class="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4">
                  <h2 class="text-white font-bold uppercase tracking-wide">{{ $t('machineDetails.sections.features', 'Features & Equipment') }}</h2>
                </div>
                <div class="p-5">
                  <ul class="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <li v-for="(feature, index) in allFeatures" :key="index" class="flex items-start gap-2 text-sm text-gray-700 bg-green-50 p-2 rounded">
                      <svg class="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                      </svg>
                      {{ feature }}
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Description -->
              <div v-if="machine.description" class="bg-white rounded-xl shadow-lg overflow-hidden">
                <div class="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4">
                  <h2 class="text-white font-bold uppercase tracking-wide">{{ $t('machineDetails.sections.description', 'Description') }}</h2>
                </div>
                <div class="p-5">
                  <div class="text-sm text-gray-700 leading-relaxed" v-html="formatDescription(machine.description)"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Similar Machines -->
      <div class="bg-gradient-to-b from-slate-200 to-slate-300 py-10 print:hidden">
        <div class="container mx-auto px-4">
          <SimilarItemsSection 
            v-if="machine"
            :category-id="machine.category?.id || machine.category_id" 
            :exclude-id="machine.id" 
            type="machines" 
          />
        </div>
      </div>

      <!-- Lightbox -->
      <Teleport to="body">
        <Transition name="fade">
          <div v-if="lightboxOpen" class="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" @click.self="closeLightbox">
            <button @click="closeLightbox" class="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
            <button v-if="machineImages.length > 1" @click="prevImage" class="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
            </button>
            <img :src="selectedImage" :alt="machine.title" class="max-w-[90vw] max-h-[90vh] object-contain transition-transform" @click="toggleZoom" :class="{ 'scale-150 cursor-zoom-out': isZoomed, 'cursor-zoom-in': !isZoomed }" />
            <button v-if="machineImages.length > 1" @click="nextImage" class="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <div class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded">
              {{ selectedImageIndex + 1 }} / {{ machineImages.length }}
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- Inquiry Modal -->
      <MachineInquiryModal v-if="showInquiryModal" :machine="inquiryMachine || machine" @close="closeInquiryModal" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';

definePageMeta({
  layout: 'default'
});

const route = useRoute();
const { getSafeImageUrl } = useImageHandling();
const { getMachineStatusLabel, getMachineStatusClasses, getMachineAvailabilityLabel } = useStatusBadge();
const config = useRuntimeConfig();

// API Base URL — always use the absolute Railway URL so SSR can call it directly
const apiBaseUrl = computed(() => config.public.apiBase || '/api/v1');
const apiBase = config.public.apiBase || 'https://technomac-snc-production.up.railway.app/api/v1';

// SSR-safe data fetching via useAsyncData — runs on the server so Google sees full content
// Falls back to client-side fetch if Railway is cold-starting (502)
const { data: machineData, pending, error, refresh } = await useAsyncData(
  `machine-${route.params.id}`,
  async () => {
    try {
      return await $fetch(`${apiBase}/machines/${route.params.id}`, {
        headers: { 'Accept': 'application/json' },
        timeout: 8000,
      });
    } catch (e) {
      // Railway cold start / 502 — return null, client will re-fetch
      if (process.server) return null;
      throw e;
    }
  },
  { server: true, lazy: false }
);

const machine = computed(() =>
  machineData.value?.data?.machine
  || machineData.value?.data?.data
  || machineData.value?.data
  || null
);

const similarMachines = computed(() =>
  machineData.value?.data?.similar_machines
  || machineData.value?.similar_machines
  || []
);

// SSR-safe URL — useRequestURL() works on both server and client
const requestUrl = useRequestURL();
const currentUrl = computed(() => {
  if (process.client) return window.location.href;
  return requestUrl.href;
});

// SEO meta — driven by SSR data so Googlebot sees the real title & description
useSeoMeta({
  title: () => machine.value
    ? `${machine.value.title}${machine.value.year_manufactured && !String(machine.value.title).includes(String(machine.value.year_manufactured)) ? ' ' + machine.value.year_manufactured : ''} - Technomac SNC`
    : 'Machine - Technomac SNC',
  description: () => machine.value?.description?.substring(0, 155)
    || (machine.value ? `${machine.value.title} - Machines graphiques chez Technomac SNC` : 'Machines graphiques occasion et neuves - Technomac SNC'),
  ogTitle: () => machine.value?.title || 'Machine - Technomac SNC',
  ogDescription: () => machine.value?.description?.substring(0, 155) || 'Machines graphiques occasion et neuves - Technomac SNC',
  ogUrl: () => `https://www.technomac-snc.com/machines/${route.params.id}`,
  ogImage: () => machine.value?.images?.[0]?.file_path
    ? getSafeImageUrl(machine.value.images[0].file_path)
    : 'https://www.technomac-snc.com/Logo_Technomac-snc_PNG.png',
  twitterCard: 'summary_large_image',
});

// JSON-LD schema — registered using useHead with raw objects.
// Unhead v2 safely serializes raw objects in innerHTML for application/ld+json without breaking quotes.
// We use unique keys to prevent conflicts with @nuxtjs/seo's default WebSite schema.
watchEffect(() => {
  const m = machine.value;
  if (m) {
    useHead({
      script: [
        {
          type: 'application/ld+json',
          key: 'product-schema',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: m.title,
            description: m.description || `${m.title} — Machine d'impression graphique`,
            image: (m.images ?? []).map(img => getSafeImageUrl(img.file_path)).filter(Boolean),
            brand: m.brand ? { '@type': 'Brand', name: m.brand.name } : undefined,
            sku: m.reference_code || String(m.id),
            itemCondition: 'https://schema.org/UsedCondition',
            offers: {
              '@type': 'Offer',
              priceCurrency: 'EUR',
              availability: m.status === 'sold'
                ? 'https://schema.org/SoldOut'
                : m.availability === 'available'
                  ? 'https://schema.org/InStock'
                  : 'https://schema.org/PreOrder',
              seller: { '@type': 'Organization', name: 'Technomac SNC', url: 'https://www.technomac-snc.com' },
            },
          }
        },
        {
          type: 'application/ld+json',
          key: 'machine-breadcrumb',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://www.technomac-snc.com/' },
              { '@type': 'ListItem', position: 2, name: 'Machines', item: 'https://www.technomac-snc.com/machines' },
              { '@type': 'ListItem', position: 3, name: m.title },
            ],
          }
        }
      ]
    });
  }
});


const isSold = computed(() => machine.value?.status === 'sold');


// UI-only state (client-side)
const selectedImageIndex = ref(0);
const lightboxOpen = ref(false);
const showInquiryModal = ref(false);
const inquiryMachine = ref(null);
const isZoomed = ref(false);

// Computed
const machineImages = computed(() => machine.value?.images || []);
const selectedImage = computed(() => {
  if (machineImages.value.length === 0) return null;
  return getImageUrl(machineImages.value[selectedImageIndex.value]);
});

const parsedFeatures = computed(() => {
  if (!machine.value?.features) return [];
  if (Array.isArray(machine.value.features)) return machine.value.features;
  if (typeof machine.value.features === 'string') {
    try {
      const parsed = JSON.parse(machine.value.features);
      return Array.isArray(parsed) ? parsed : [machine.value.features];
    } catch {
      return machine.value.features.split('\n').filter(f => f.trim());
    }
  }
  return [];
});

const parsedSpecifications = computed(() => {
  if (!machine.value?.specifications) return {};
  if (typeof machine.value.specifications === 'object') return machine.value.specifications;
  if (typeof machine.value.specifications === 'string') {
    try { return JSON.parse(machine.value.specifications); } catch { return {}; }
  }
  return {};
});

const allFeatures = computed(() => {
  const features = [...parsedFeatures.value];
  if (parsedSpecifications.value) {
    Object.values(parsedSpecifications.value).forEach(val => {
      if (Array.isArray(val)) features.push(...val);
    });
  }
  return features;
});

// Check if any technical specifications exist
const hasTechnicalSpecs = computed(() => {
  if (!machine.value) return false;
  
  const specFields = [
    'machine_model',
    'machine_type',
    'max_sheet_size',
    'min_sheet_size',
    'max_printing_area',
    'plate_size',
    'max_printing_speed',
    'operating_speed',
    'power_requirement',
    'machine_dimensions',
    'machine_weight'
  ];
  
  // Check if any spec field has a value
  const hasBasicSpecs = specFields.some(field => machine.value[field]);
  
  // Check if parsedSpecifications has any non-array values
  const hasAdditionalSpecs = Object.entries(parsedSpecifications.value).some(
    ([_, value]) => !Array.isArray(value) && value
  );
  
  return hasBasicSpecs || hasAdditionalSpecs;
});

const isArrayValue = (value) => Array.isArray(value);
const formatSpecKey = (key) => key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();

// Methods
const getImageUrl = (image) => {
  if (!image) return '/images/placeholder-machine.svg';
  // Prefer file_path, always process through getSafeImageUrl
  if (image.file_path) return getSafeImageUrl(image.file_path);
  if (image.url) return getSafeImageUrl(image.url);
  if (image.full_url) return getSafeImageUrl(image.full_url);
  return '/images/placeholder-machine.svg';
};

const handleImageError = (e) => { e.target.src = '/images/placeholder-machine.svg'; };

// Status badge styling - maps actual API status values to visual styles
const getStatusBadgeStyles = (status) => {
  const classes = {
    published: 'bg-green-100 text-green-800 border border-green-300',
    sold: 'bg-red-100 text-red-800 border border-red-300',
    reserved: 'bg-amber-100 text-amber-800 border border-amber-300',
    draft: 'bg-gray-100 text-gray-600 border border-gray-300',
    archived: 'bg-gray-100 text-gray-600 border border-gray-300'
  };
  return classes[status] || 'bg-gray-100 text-gray-800 border border-gray-300';
};

const getStatusDotClass = (status) => {
  const classes = { published: 'bg-green-500', sold: 'bg-red-500', reserved: 'bg-amber-500', draft: 'bg-gray-400', archived: 'bg-gray-400' };
  return classes[status] || 'bg-gray-500';
};

// Availability badge styling - maps availability values to visual styles
const getAvailabilityBadgeStyles = (availability) => {
  const classes = {
    immediately: 'bg-green-100 text-green-800 border border-green-300',
    in_production: 'bg-blue-100 text-blue-800 border border-blue-300',
    sold: 'bg-red-100 text-red-800 border border-red-300'
  };
  return classes[availability] || classes.immediately;
};

const getAvailabilityDotClass = (availability) => {
  const classes = { immediately: 'bg-green-500', in_production: 'bg-blue-500', sold: 'bg-red-500' };
  return classes[availability] || 'bg-green-500';
};

const formatDescription = (text) => text ? text.replace(/\n/g, '<br>') : '';

const prevImage = () => { selectedImageIndex.value = (selectedImageIndex.value - 1 + machineImages.value.length) % machineImages.value.length; };
const nextImage = () => { selectedImageIndex.value = (selectedImageIndex.value + 1) % machineImages.value.length; };

const openLightbox = (index) => {
  selectedImageIndex.value = index;
  lightboxOpen.value = true;
  isZoomed.value = false;
  if (process.client) document.body.style.overflow = 'hidden';
};

const closeLightbox = () => {
  lightboxOpen.value = false;
  isZoomed.value = false;
  if (process.client) document.body.style.overflow = '';
};

const toggleZoom = () => { isZoomed.value = !isZoomed.value; };

const openInquiryModal = () => { inquiryMachine.value = null; showInquiryModal.value = true; };
const openInquiryModalForMachine = (machineData) => { inquiryMachine.value = machineData; showInquiryModal.value = true; };
const closeInquiryModal = () => { showInquiryModal.value = false; inquiryMachine.value = null; };
const printPage = () => { if (process.client) window.print(); };

const handleKeydown = (e) => {
  if (lightboxOpen.value) {
    if (e.key === 'ArrowLeft') prevImage();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'Escape') closeLightbox();
  }
};

onMounted(() => {
  if (process.client) window.addEventListener('keydown', handleKeydown);
  // Re-fetch client-side if SSR failed (Railway cold start)
  if (!machineData.value) {
    refresh();
  }
});

onUnmounted(() => {
  if (process.client) {
    window.removeEventListener('keydown', handleKeydown);
    document.body.style.overflow = '';
  }
});

// Re-fetch when navigating between machine pages client-side
watch(() => route.params.id, () => { refresh(); selectedImageIndex.value = 0; });
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@media print {
  .print\:hidden { display: none !important; }
}
</style>
